import { useId, useState } from 'react'
import type { ReceiptItem } from '@domain/receipt'
import type { Category } from '@domain/finance'
import { formatBRL } from '@shared/money/format'
import { centsToInput, parseAmountInput } from '@shared/money/parse'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

/**
 * A linha que a pessoa acrescenta à mão — frete, taxa, abatimento.
 *
 * ## O que ela existe para resolver
 *
 * Uma nota de R$ 124,17 cujos itens somam R$ 102,18: os R$ 21,99 que faltam são
 * a entrega. Sem um lugar para dizer isso, a revisão acusa "a soma está longe do
 * total, quase sempre é leitura errada" — e não era. O contrário também
 * acontece: o abatimento do caixa que não pertence a item nenhum.
 *
 * ## Soma ou abate é BOTÃO, e não um menos digitado
 *
 * O valor entra sempre positivo, e o que ele faz com o total é uma escolha
 * visível. Num teclado de celular o menos se esconde, e um menos perdido no
 * meio dos dígitos inverte a conta sem nada na tela mudando de cara. Escolha do
 * dono do projeto.
 *
 * ## Abater EXIGE categoria, e tem teto
 *
 * O abatimento sai de alguma categoria do detalhamento — `confirmReceipt`
 * recusa a categoria que fecharia negativa, e essa recusa acontece na Cloud
 * Function, com a nota já confirmada: a pessoa veria uma nota confirmada e um
 * detalhamento que nunca chegou. Aqui a pergunta é feita antes, com `teto`
 * saindo de `totalPorCategoria` — a mesma composição que a confirmação usa.
 *
 * Somar não tem essa exigência: linha sem categoria é caso normal, e vai para o
 * resto como qualquer outra.
 */
export function ItemManual({
  linha,
  categorias,
  sugestaoCents,
  teto,
  onSalvar,
  onCriouCategoria,
  onFechar,
}: {
  /** A linha manual sendo editada, ou `null` para uma nova. */
  linha: ReceiptItem | null
  categorias: Category[]
  /**
   * O valor que o campo já traz: a diferença que falta na nota.
   *
   * É o caso que motivou a tela inteira — quem abre isto numa nota que não
   * fecha quase sempre vai digitar exatamente esse número. Zero deixa o campo
   * vazio.
   */
  sugestaoCents: number
  /**
   * Quanto a categoria escolhida já tem nesta nota, **sem contar a linha em
   * edição**. É o teto de um abatimento nela.
   */
  teto: (categoryId: string | null) => number
  onSalvar: (input: { name: string; totalCents: number; categoryId: string | null }) => void
  onCriouCategoria: () => void
  onFechar: () => void
}) {
  const id = useId()
  const editando = linha !== null
  const [nome, setNome] = useState(linha?.rawText ?? '')
  const [valor, setValor] = useState(
    linha === null
      ? sugestaoCents === 0
        ? ''
        : centsToInput(Math.abs(sugestaoCents))
      : centsToInput(Math.abs(linha.totalCents)),
  )
  // A diferença negativa quer dizer que os itens somam MAIS que a nota, e o que
  // conserta isso é um abatimento. Abrir em "Soma" ali faria o primeiro toque
  // ser sempre para corrigir o lado.
  const [abate, setAbate] = useState(linha === null ? sugestaoCents < 0 : linha.totalCents < 0)
  const [categoryId, setCategoryId] = useState<string | null>(linha?.categoryId ?? null)
  /**
   * A folha abre calada.
   *
   * Sem isto ela nasce dizendo "dê um nome à linha" — uma reclamação sobre algo
   * que ninguém teve chance de fazer ainda. O botão desabilitado já diz que
   * falta alguma coisa; a frase só aparece quando há uma resposta para
   * criticar.
   */
  const [mexeu, setMexeu] = useState(false)

  const lido = parseAmountInput(valor)
  const cents = lido.status === 'ok' ? Math.abs(lido.cents) : null
  const disponivel = teto(categoryId)

  const erro =
    nome.trim() === ''
      ? 'Dê um nome à linha — é ele que aparece na lista e no detalhamento.'
      : cents === null || cents === 0
        ? lido.status === 'invalid'
          ? lido.message
          : 'Digite quanto esta linha vale.'
        : abate && categoryId === null
          ? 'Um abatimento precisa de categoria: é de lá que ele sai no detalhamento.'
          : abate && cents > disponivel
            ? `Esta categoria tem ${formatBRL(disponivel)} nesta nota, e o abatimento é maior. Divida em duas linhas ou escolha outra.`
            : null

  const assinado = cents === null ? null : abate ? -cents : cents

  return (
    <FolhaDeBaixo titulo={editando ? `Editar ${linha.rawText}` : 'Adicionar item'} onFechar={onFechar}>
      <form
        className="flex flex-col gap-3 p-1"
        onSubmit={(e) => {
          e.preventDefault()
          // A tentativa de enviar revela o erro — decisão 311. O botão ficava
          // desabilitado com cara de habilitado, e a folha calada (de propósito,
          // para não reclamar antes da hora): tocar não fazia nada e não dizia
          // por quê.
          if (erro !== null || assinado === null) {
            setMexeu(true)
            return
          }
          onSalvar({ name: nome.trim(), totalCents: assinado, categoryId })
          onFechar()
        }}
      >
        <p className="text-xs text-[var(--text-muted)]">
          Para o que a nota cobra e a leitura não lista — frete, taxa de serviço, embalagem — ou
          para um abatimento que não é de nenhum item.
        </p>

        <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]" htmlFor={`${id}-nome`}>
          Nome
          <input
            id={`${id}-nome`}
            value={nome}
            placeholder="Frete"
            autoComplete="off"
            onChange={(e) => {
              setNome(e.target.value)
              setMexeu(true)
            }}
            className={CAMPO}
          />
        </label>

        <div className="flex items-end gap-2">
          <label
            className="flex flex-1 flex-col gap-1 text-xs text-[var(--text-muted)]"
            htmlFor={`${id}-valor`}
          >
            Valor
            <input
              id={`${id}-valor`}
              inputMode="decimal"
              value={valor}
              onChange={(e) => {
                setValor(e.target.value)
                setMexeu(true)
              }}
              className={CAMPO}
            />
          </label>

          {/* Dois botões, e não um `<select>`: o estado precisa ser legível sem
              abrir nada, porque é ele que decide o sinal da conta. */}
          <div role="group" aria-label="O que este valor faz com o total" className="fileira-de-icones">
            <Sinal
              ativo={!abate}
              onClick={() => {
                setAbate(false)
                setMexeu(true)
              }}
            >
              Soma
            </Sinal>
            <Sinal
              ativo={abate}
              onClick={() => {
                setAbate(true)
                setMexeu(true)
              }}
            >
              Abate
            </Sinal>
          </div>
        </div>

        <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]">
          Categoria
          <SeletorDeCategoria
            categorias={categorias}
            value={categoryId}
            rotulo={`Categoria da linha: ${categorias.find((c) => c.id === categoryId)?.name ?? 'sem categoria'}`}
            onChange={(id) => {
              setCategoryId(id)
              setMexeu(true)
            }}
            onCriou={onCriouCategoria}
          />
        </label>

        {mexeu && erro !== null ? (
          <p className="text-xs" style={{ color: 'var(--pendente)' }} role="alert">
            {erro}
          </p>
        ) : (
          erro === null &&
          assinado !== null && (
            <p className="text-xs text-[var(--text-muted)] tabular-nums">
              {`A soma dos itens ${abate ? 'cai' : 'sobe'} ${formatBRL(Math.abs(assinado))}.`}
            </p>
          )
        )}

        <div className="flex flex-wrap gap-2">
          <Botao tom="primario" type="submit">
            {editando ? 'Salvar' : 'Adicionar'}
          </Botao>
          <button
            type="button"
            onClick={onFechar}
            className="alvo px-3 text-sm text-[var(--text-muted)]"
          >
            Cancelar
          </button>
        </div>
      </form>
    </FolhaDeBaixo>
  )
}

// `min-h-11`: 44px, o alvo de toque do sistema — os campos mediam 40,85px.
const CAMPO =
  'min-h-11 w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm tabular-nums'

/** Um dos dois lados do sinal. `aria-pressed` porque é estado, não navegação. */
function Sinal({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className="alvo rounded-lg border px-3 text-sm"
      style={
        ativo
          ? { borderColor: 'var(--brand)', color: 'var(--brand)' }
          : { borderColor: 'var(--border)', color: 'var(--text-muted)' }
      }
    >
      {children}
    </button>
  )
}
